import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Layers, CalendarClock, MailCheck, CalendarRange, Info, CheckCheck, Settings2,
  ArrowUpRight, MessageCircle, X, Award, ClipboardCheck, AlertTriangle, ScrollText, Receipt,
} from 'lucide-react';
import { AsyncBoundary, Drawer, useToast } from '@kollez/design-system';
import { useNotifications, useMarkNotificationRead, useMarkNotificationUnread, useMarkAllRead, type NotificationRow } from '@/lib/queries';
import { formatTime } from '@kollez/shared';

/**
 * /notifications (F92b) — direct port of kollez/pages/shared/notifications.html.
 *   Tabs (Unread / All) + chips-row for type filter · day-grouped list with icon + unread dot
 *   Per-item drawer with related-record card + Open/Mark unread/Mute footer.
 *   Mark-all-as-read + notification-prefs shortcut in header.
 */

const TYPES: Record<string, { label: string; Icon: typeof Layers }> = {
  application: { label: 'Applications', Icon: Layers },
  interview: { label: 'Interviews', Icon: CalendarClock },
  offer: { label: 'Offers', Icon: MailCheck },
  badge: { label: 'Badges', Icon: Award },
  drive: { label: 'Drives', Icon: CalendarRange },
  assessment: { label: 'Assessments', Icon: ClipboardCheck },
  integrity: { label: 'Integrity', Icon: AlertTriangle },
  billing: { label: 'Billing', Icon: Receipt },
  compliance: { label: 'Compliance', Icon: ScrollText },
  system: { label: 'System', Icon: Info },
};

function typeKey(t: string): string {
  const s = t.toLowerCase();
  if (s.startsWith('applic')) return 'application';
  if (s.startsWith('interv')) return 'interview';
  if (s.startsWith('offer')) return 'offer';
  // Step 66 — Badge notifications (badge_earned, badge_revoked) get their own filter chip
  // rather than falling through to 'system'. Credentials are the student-facing centerpiece
  // of v1.6; the filter should surface them, not bury them.
  if (s.startsWith('badge')) return 'badge';
  if (s.startsWith('drive')) return 'drive';
  // Ship 5g emits assessment_* + integrity_flag_*; 5h emits invoice_* + payment_* +
  // report_* + mou_expiring; each gets its own chip so students / TPO / group admins
  // don't have to hunt through the 'system' bucket.
  if (s.startsWith('assessment')) return 'assessment';
  if (s.startsWith('integrity')) return 'integrity';
  if (s.startsWith('invoice') || s.startsWith('payment')) return 'billing';
  if (s.startsWith('mou') || s.startsWith('report') || s.startsWith('compliance')) return 'compliance';
  return 'system';
}

function dayLabel(dateISO: string): string {
  const d = new Date(dateISO);
  const now = new Date();
  const midNow = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dayDiff = Math.floor((midNow.getTime() - new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()) / 86400000);
  if (dayDiff === 0) return 'Today';
  if (dayDiff === 1) return 'Yesterday';
  if (dayDiff <= 7) return d.toLocaleString('en-IN', { weekday: 'long' });
  return 'Earlier';
}

export function NotificationsPage() {
  const { data, isLoading, error, refetch } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markUnread = useMarkNotificationUnread();
  const markAll = useMarkAllRead();
  const toast = useToast();
  const [tab, setTab] = useState<'unread' | 'all'>('unread');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [openId, setOpenId] = useState<string | null>(null);

  const rows = data?.notifications ?? [];
  const unread = data?.unread ?? rows.filter((r) => !r.readAt).length;

  const shown = useMemo(() => {
    return rows.filter((r) => {
      const okTab = tab === 'all' || !r.readAt;
      const okType = !typeFilter || typeKey(r.type) === typeFilter;
      return okTab && okType;
    });
  }, [rows, tab, typeFilter]);

  const grouped = useMemo(() => {
    const g: Record<string, NotificationRow[]> = {};
    for (const r of shown) {
      const k = dayLabel(r.createdAt);
      (g[k] ??= []).push(r);
    }
    return g;
  }, [shown]);

  const opened = openId ? rows.find((r) => r._id === openId) : null;
  const openedTypeMeta = opened ? TYPES[typeKey(opened.type)] ?? TYPES.system! : null;

  async function open(row: NotificationRow) {
    setOpenId(row._id);
    if (!row.readAt) {
      try { await markRead.mutateAsync(row._id); } catch { /* ignore */ }
    }
  }

  async function readAll() {
    if (unread === 0) return;
    try {
      await markAll.mutateAsync();
      toast.show({ message: `${unread} marked as read` });
    } catch { toast.show({ tone: 'warning', message: 'Could not mark all' }); }
  }

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Notifications</h1>
          <p className="subhead">Updates from your drives, applications and students.</p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-outline" onClick={readAll} disabled={unread === 0 || markAll.isPending}>
            <CheckCheck size={14} />Mark all as read
          </button>
          <Link className="btn btn-ghost btn-icon" to="/settings/privacy" aria-label="Notification preferences">
            <Settings2 size={14} />
          </Link>
        </div>
      </div>

      <div className="card">
        <div className="px-4 pt-1">
          <div className="tabs" style={{ borderBottom: 0 }} role="tablist">
            <button className="tab" role="tab" aria-selected={tab === 'unread'} onClick={() => setTab('unread')}>
              Unread {unread > 0 && <span className="count">{unread}</span>}
            </button>
            <button className="tab" role="tab" aria-selected={tab === 'all'} onClick={() => setTab('all')}>All</button>
          </div>
        </div>

        <div className="chips-row border-t px-4 py-2 flex flex-wrap gap-2" role="group" aria-label="Filter by type">
          <button className="tag" aria-pressed={!typeFilter} onClick={() => setTypeFilter('')}>All types</button>
          {Object.entries(TYPES).map(([k, meta]) => (
            <button key={k} className="tag" aria-pressed={typeFilter === k} onClick={() => setTypeFilter(k)}>
              {meta.label}
            </button>
          ))}
        </div>

        <AsyncBoundary
          kind="table"
          inCard
          loading={isLoading}
          error={error}
          empty={!isLoading && shown.length === 0}
          emptyTitle={tab === 'unread' ? 'No unread notifications' : 'Nothing of this type yet'}
          emptyHint={tab === 'unread' ? "You've read everything. Older updates are under All." : 'Try another type.'}
          onRetry={() => void refetch()}
        >
          <div>
            {Object.entries(grouped).map(([day, items]) => (
              <div key={day}>
                <div className="border-t border-line-tertiary px-5 py-2 text-xs font-semibold text-ink-tertiary" style={{ background: 'rgb(var(--bg-secondary))' }}>
                  {day}
                </div>
                {items.map((i) => {
                  const meta = TYPES[typeKey(i.type)] ?? TYPES.system!;
                  const Icon = meta.Icon;
                  const isUnread = !i.readAt;
                  return (
                    <div key={i._id} className="relative overflow-hidden border-t border-line-tertiary">
                      <button
                        type="button"
                        className="flex w-full items-start gap-3 px-5 py-3.5 text-left hover:bg-secondary"
                        style={{ background: 'rgb(var(--bg-primary))' }}
                        onClick={() => open(i)}
                      >
                        <span
                          className="mt-0.5 grid h-8 w-8 flex-shrink-0 place-items-center rounded-md border border-line-tertiary text-ink-secondary"
                        >
                          <Icon size={14} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className={`block ${isUnread ? 'font-semibold' : 'text-ink-secondary'}`}>{i.title}</span>
                          <span className="block truncate text-sm text-ink-tertiary">{i.body}</span>
                          {i.action?.cta && (
                            <span className="mt-1 block text-xs text-ink-tertiary">{i.action.cta}</span>
                          )}
                        </span>
                        <span className="flex flex-col items-end gap-2">
                          <span className="text-xs text-ink-tertiary">{formatTime(i.createdAt)}</span>
                          {isUnread && <span className="dot dot-accent" role="img" aria-label="Unread" />}
                        </span>
                      </button>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </AsyncBoundary>
      </div>

      <p className="helper show-mobile">Tap a notification to open it and jump to the related record.</p>

      <Drawer open={Boolean(opened)} onClose={() => setOpenId(null)} title={opened?.title ?? ''} size="md">
        {opened && openedTypeMeta && (
          <>
            <div className="flex items-start gap-3">
              <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-md border border-line-tertiary">
                <openedTypeMeta.Icon size={14} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-ink-tertiary">
                  {openedTypeMeta.label}, {dayLabel(opened.createdAt).toLowerCase()} at {formatTime(opened.createdAt)}
                </p>
                <h2 className="mt-1 text-lg font-semibold leading-snug">{opened.title}</h2>
              </div>
            </div>
            <p className="mt-4">{opened.body}</p>
            {opened.action?.url && (
              <div className="card card-pad mt-4">
                <p className="text-xs text-ink-tertiary">Related record</p>
                <p className="mt-1 font-semibold">{opened.action.cta ?? 'Open'}</p>
                <Link className="link mt-2 inline-flex items-center gap-1 text-sm" to={opened.action.url}>
                  Open <ArrowUpRight size={12} />
                </Link>
              </div>
            )}
            <div className="notice notice-info mt-4">
              <MessageCircle size={14} />
              <div>Also sent to WhatsApp and email. <Link className="link" to="/settings/privacy">Change this</Link></div>
            </div>
            <div className="drawer-foot">
              {opened.action?.url && (
                <Link className="btn btn-primary" to={opened.action.url}>Open record</Link>
              )}
              <button
                className="btn btn-outline"
                onClick={async () => {
                  const id = opened._id;
                  setOpenId(null);
                  try {
                    await markUnread.mutateAsync(id);
                    toast.show({ message: 'Marked as unread' });
                  } catch {
                    toast.show({ tone: 'warning', message: 'Could not mark as unread' });
                  }
                }}
              >
                <X size={14} />Mark as unread
              </button>
              {/* Step 61 — the per-type "Mute" button was toast-only theater; muting requires a
                  NotificationPreference collection with a matrix per (type × channel) and a
                  server-side gate on every writer. That's a real vertical, not a drawer button.
                  Dropped until the prefs page ships. Users who want fewer notifications land on
                  /settings/privacy via the notice above. */}
              <Link className="btn btn-ghost ml-auto" to="/settings/privacy">
                <Settings2 size={14} />Notification settings
              </Link>
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
}
